/*
 * np-chrome.js — shared behaviour for the design header, mega menu, mobile
 * drawer, search and slide cart (sections/np-header.liquid and the snippets
 * it renders: np-search, np-predictive-search, np-slide-cart, np-mg-link).
 *
 * Loaded with <script src="{{ 'np-chrome.js' | asset_url }}" defer></script>
 * from np-header.liquid, which renders first in <body>, so this file runs
 * before other sections' deferred scripts and window.npCart / npSlideCart
 * are ready by the time any other section's script calls them.
 *
 * No Liquid in this file. Per-page data comes in via
 * <script type="application/json" id="...-config"> data islands and
 * data-* attributes rendered by the Liquid files above.
 *
 * ES5 (var/function), ships to every page, guards for missing elements.
 */
(function () {
  "use strict";
  if (window.__npChromeLoaded) return;
  window.__npChromeLoaded = true;

  /* ================================================================
     SHARED UTILITIES (one esc() / money() for the whole file — cart,
     search and the mega menu/drawer link tags all use these).
     ================================================================ */
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  var JSON_HEADERS = { "Content-Type": "application/json", "Accept": "application/json" };

  function $(id) { return document.getElementById(id); }
  function qs(root, sel) { return root ? root.querySelector(sel) : null; }
  function qsa(root, sel) { return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : []; }

  function esc(t) {
    return String(t == null ? "" : t).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* cart rows/labels are tight: drop the brand prefix and the marketing tail
     after the first pipe or " - ", so long PDP-style titles read short. */
  function shortName(t) {
    var out = String(t == null ? "" : t).split("|")[0].split(" - ")[0].replace(/^\s*EFECTIV\s*(®|™)?\s*/i, "").trim();
    return out || String(t == null ? "" : t);
  }

  function money(cents, format) {
    var v = (Number(cents) / 100).toFixed(2);
    var parts = v.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    var out = parts.join(".");
    if (!format) return out;
    return format.replace(/\{\{\s*amount[^}]*\}\}/, out);
  }

  /* ---- packs (Essentials Starter Pack): 9 Oct pre-launch check, Adam: fix them all ----
     new-bundle-pdp.liquid adds the pack as one line carrying _bundle_parent plus the
     shopper's choices ("Whey Protein Powder": "Biscuit Spread"), and each component as
     its own line carrying _bundle_parent and _bundle_component (the pack's product id).
     The pack's code (a Buy X Get Y) only works while every component is there, so the
     components get no controls of their own and move with the pack (slide cart below,
     np-cart-page.js). The same helpers are in np-cart-page.js, keep the two in step. */
  function isComp(it) { return !!(it.properties && it.properties._bundle_component); }
  function isPack(it) { return !!(it.properties && it.properties._bundle_parent) && !isComp(it); }
  /* component lines this pack added: same bundle id, and the pack's choice for that product matches the line */
  function ownedComps(items, pack) {
    return items.filter(function (c) {
      if (!isComp(c) || String(c.properties._bundle_component) !== String(pack.properties._bundle_parent)) return false;
      var slot = String(c.product_title || "").replace(/EFECTIV®|EFECTIV/g, "").split("|")[0].split(" - ")[0].trim();
      var label = String(c.variant_title || "").split(" / ").pop().trim();
      var pick = pack.properties[slot];
      return pick === slot || pick === label;
    });
  }
  /* items as a shopper counts them (12 Oct 2026): a pack is one item, its component lines are not extra items.
     A component with no pack in the cart is an ordinary line and counts. */
  function itemCount(cart) {
    var items = (cart && cart.items) || [], hidden = {};
    items.forEach(function (it) {
      if (isPack(it)) ownedComps(items, it).forEach(function (c) { hidden[c.key] = true; });
    });
    return items.reduce(function (n, i) { return n + (hidden[i.key] ? 0 : i.quantity); }, 0);
  }
  /* The packs' codes are Buy X Get Y with a limit of 3 uses per order (store discounts EssentialsStarterPack and
     CrossFitAthleteStack, usesPerOrderLimit 3): a 4th pack is charged in full. Keep this equal to that limit.
     (12 Oct 2026, coordinator follow-up) */
  var PACK_MAX = 3;
  var PACK_MAX_NOTE = "Up to " + PACK_MAX + " packs per order.";
  /* how many more of this pack the order can take: the limit counts every pack line of the same bundle */
  function packRoom(items, pack) {
    var n = 0;
    items.forEach(function (i) {
      if (isPack(i) && String(i.properties._bundle_parent) === String(pack.properties._bundle_parent)) n += i.quantity;
    });
    return PACK_MAX - n;
  }

  function readJSON(id) {
    var el = $(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent || "{}"); } catch (e) { return null; }
  }

  /* html-fetch helper: rejects with a friendly Error, distinguishing a 422
     (Shopify's own validation message) from any other failure. */
  function fetchJSON(url, init) {
    return fetch(url, init).then(function (res) {
      if (res.status === 422) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          throw new Error(body.description || body.message || "Something went wrong. Please try again.");
        });
      }
      if (!res.ok) throw new Error("Something went wrong. Please try again.");
      return res.json();
    });
  }

  /* html/lock a single scroll-lock counter shared by every dialog, so two
     panels opening in quick succession (e.g. drawer closing while cart
     opens) never leave the page stuck locked or unlocked. */
  var lockCount = 0;
  function lockScroll(on) {
    lockCount = Math.max(0, lockCount + (on ? 1 : -1));
    document.documentElement.style.overflow = lockCount > 0 ? "hidden" : "";
  }

  /* ---- generic off-canvas dialog (drawer + cart share this) ----
     Keeps the panel display:none until first opened (so lazy images and
     fixed-position content inside it never load at page load), animates
     via the panel's own is-open CSS transition, traps Tab, restores focus
     to the opener on close, and locks scroll while open. */
  function Dialog(panel, scrim, opts) {
    opts = opts || {};
    var self = this;
    var opener = null;
    var hideTimer = null;
    var hideFn = null;
    var rafId = 0;
    /* a close that is still animating must not hide a panel that was reopened
       in the meantime (double-click, close then quick reopen) */
    function cancelPendingHide() {
      clearTimeout(hideTimer);
      if (hideFn) { panel.removeEventListener("transitionend", hideFn); hideFn = null; }
    }
    this.isOpen = false;
    this.everOpened = false;
    /* a click on the dimmed backdrop closes the panel (slide cart, menu drawer) */
    if (scrim) scrim.addEventListener("click", function () { self.close(); });

    function focusables() {
      return qsa(panel, 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
        .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    }
    function onKeydown(e) {
      if (e.key === "Escape") { e.preventDefault(); self.close(); return; }
      if (e.key === "Tab") {
        var f = focusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    this.open = function () {
      if (self.isOpen) return;
      self.isOpen = true;
      opener = document.activeElement;
      cancelPendingHide();
      var firstOpen = !self.everOpened;
      self.everOpened = true;
      if (panel.style.display === "none") panel.style.display = "";
      if (opts.onFirstOpen && firstOpen) opts.onFirstOpen();
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function () {
        if (!self.isOpen) return;
        panel.classList.add("is-open");
        if (scrim) scrim.classList.add("is-open");
      });
      panel.setAttribute("aria-hidden", "false");
      lockScroll(true);
      document.addEventListener("keydown", onKeydown, true);
      setTimeout(function () {
        var f = focusables();
        (f[0] || panel).focus();
      }, 60);
      if (opts.onOpen) opts.onOpen();
    };

    this.close = function () {
      if (!self.isOpen) return;
      self.isOpen = false;
      cancelAnimationFrame(rafId);
      panel.classList.remove("is-open");
      if (scrim) scrim.classList.remove("is-open");
      panel.setAttribute("aria-hidden", "true");
      lockScroll(false);
      document.removeEventListener("keydown", onKeydown, true);
      cancelPendingHide();
      hideFn = function (e) {
        if (e && e.target !== panel) return;
        cancelPendingHide();
        if (!self.isOpen) panel.style.display = "none";
      };
      panel.addEventListener("transitionend", hideFn);
      /* reduced-motion / no matching transition still hides the panel */
      hideTimer = setTimeout(hideFn, 500);
      if (opener && typeof opener.focus === "function") opener.focus();
      if (opts.onClose) opts.onClose();
    };
  }

  window.npChromeUtil = { esc: esc, money: money, shortName: shortName, lockScroll: lockScroll, Dialog: Dialog, fetchJSON: fetchJSON, $: $, qs: qs, qsa: qsa };

  /* ================================================================
     CART API — window.npCart, the contract every section adds to cart
     through. Also owns the np:cart broadcast every add/update uses.
     ================================================================ */
  (function () {
    function dispatchCart(cart) {
      document.documentElement.dispatchEvent(new CustomEvent("np:cart", { bubbles: true, detail: { cart: cart } }));
    }

    function getCart() {
      return fetchJSON(ROOT + "cart.js", { headers: { Accept: "application/json" } });
    }

    /* cart/update.js sets the WHOLE list of codes, so the codes already on the cart go with the new one
       (12 Oct 2026, coordinator follow-up; 9 Oct pre-launch check, Adam: fix them all). Before, adding a
       pack or a bundle replaced every code on the cart: a pack added after another pack took the first
       pack's code away, and a code the shopper had typed was lost. Shopify decides which of several codes
       that cannot combine applies; the others stay on the cart as not valid and the shopper can remove them. */
    function applyDiscount(code) {
      return getCart().then(function (c) {
        var codes = (c.discount_codes || []).map(function (d) { return d.code; });
        if (!codes.some(function (x) { return String(x).toLowerCase() === String(code).toLowerCase(); })) codes.push(code);
        return fetchJSON(ROOT + "cart/update.js", {
          method: "POST",
          headers: JSON_HEADERS,
          body: JSON.stringify({ discount: codes.join(",") })
        });
      }).then(function (cart) {
        dispatchCart(cart);
        return cart;
      });
    }

    function release(button) {
      if (!button) return;
      button.disabled = false;
      delete button.__npCartPromise;
    }

    /* one live region for cart errors (Shopify's own message, e.g. stock
       limits), created up front so screen readers announce the first one */
    var toast = document.createElement("div");
    var toastTimer = 0;
    toast.className = "np-toast";
    toast.setAttribute("role", "alert");
    document.body.appendChild(toast);
    function notify(msg) {
      toast.textContent = msg;
      toast.classList.add("is-on");
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { toast.classList.remove("is-on"); }, 5000);
    }

    function add(items, opts) {
      opts = opts || {};
      var button = opts.button;
      if (button && button.__npCartPromise) return button.__npCartPromise;
      if (button) button.disabled = true;

      var promise = fetchJSON(ROOT + "cart/add.js", {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ items: items })
      }).then(function () {
        return opts.discount ? applyDiscount(opts.discount) : getCart();
      }).then(function (cart) {
        dispatchCart(cart);
        if (opts.open !== false) {
          if (window.npSlideCart) window.npSlideCart.open();
          else window.location.href = ROOT + "cart";
        }
        return cart;
      });

      promise = promise.then(
        function (cart) { release(button); return cart; },
        function (err) {
          release(button);
          var e = err instanceof Error ? err : new Error("Something went wrong. Please try again.");
          if (opts.notify !== false) notify(e.message);
          throw e;
        }
      );

      if (button) button.__npCartPromise = promise;
      return promise;
    }

    /* ---- Klarna on-site messaging ("pay in 3"): the app block on the cart page ----
       Klarna's own script mounts the placement once and gives it its locale and amount, so the element is never swapped
       for a fresh one (the server's copy has no locale and stays blank). Only its amount, in pence, is changed here after
       every cart change: np-cart-page.js after its own writes, and every np:cart (slide cart, any other section).
       Klarna drops an amount that arrives while its previous request is still in flight (placement.updatePending): the
       attribute then holds the new total while the old figure stays on screen, and a request that fails leaves the flag
       set for good. So the amount is set only while the placement is idle, and when the attribute is already right but the
       picture is not (Klarna's own cart watcher got there first), the placement is asked to draw again (updateContent).
       Klarna.OnsiteMessaging.refresh() is called as well where the library has one. Everything is guarded: no placement,
       no Klarna library or a different version means nothing happens and nothing throws. */
    var KLARNA_PLACEMENTS = ".ncart-app klarna-placement, #npc klarna-placement";
    var KLARNA_AMOUNT = "data-purchase-amount";
    function klarnaApply(kp, tries) {
      try {
        var amt = kp.__npWant;
        if (!amt) return;
        if (kp.__npShown === undefined) kp.__npShown = kp.getAttribute(KLARNA_AMOUNT);   /* what Klarna drew at set-up */
        if (kp.__npShown === amt && kp.getAttribute(KLARNA_AMOUNT) === amt) return;
        if (kp.updatePending === true && tries < 24) {
          kp.__npTimer = setTimeout(function () { klarnaApply(kp, tries + 1); }, 200);
          return;
        }
        if (kp.updatePending === true) kp.updatePending = false;
        if (kp.getAttribute(KLARNA_AMOUNT) !== amt) kp.setAttribute(KLARNA_AMOUNT, amt);
        else if (typeof kp.updateContent === "function") {
          var drawn = kp.updateContent();
          if (drawn && typeof drawn.catch === "function") drawn.catch(function () {});
        }
        kp.__npShown = amt;
        var k = window.Klarna && window.Klarna.OnsiteMessaging;
        if (k && typeof k.refresh === "function") k.refresh();
      } catch (e) {}
    }
    function syncKlarna(total) {
      try {
        var n = Math.round(Number(total));
        if (!isFinite(n) || n <= 0) return;   /* Klarna rejects 0, and an empty cart shows no placement */
        qsa(document, KLARNA_PLACEMENTS).forEach(function (kp) {
          kp.__npWant = String(n);
          clearTimeout(kp.__npTimer);
          klarnaApply(kp, 0);
        });
      } catch (e) {}
    }
    document.documentElement.addEventListener("np:cart", function (e) {
      var c = e.detail && e.detail.cart;
      if (c) syncKlarna(c.total_price);
    });

    /* itemCount: what the header and the home sticky bar both show (a pack is one item); cart(): the cart as the slide cart holds it */
    window.npCart = { add: add, applyDiscount: applyDiscount, getCart: getCart, notify: notify, packMax: PACK_MAX, packMaxNote: PACK_MAX_NOTE, itemCount: itemCount, syncKlarna: syncKlarna, cart: function () { return null; } };
  })();

  /* ---- Build a Bundle funnel events (Adam 19 Oct 2026). The builders (np-bundle-page.js, np-home.js) call
     window.npTrack(name, data). Each event goes to Google Analytics G-1LVTDYJJ13 through the gtag the Google
     channel loads on the page (so it follows the visitor's consent choice) and to Shopify's customer events
     (Shopify.analytics.publish) for any pixel that wants it later. Events: bundle_started, bundle_tier_12,
     bundle_tier_16, bundle_tier_20 (each once per page view), bundle_added_to_cart. ---- */
  window.npTrack = function (name, data) {
    var d = data || {};
    try { if (typeof window.gtag === "function") window.gtag("event", name, Object.assign({ send_to: "G-1LVTDYJJ13" }, d)); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, d); } catch (e) {}
  };

  /* ================================================================
     DISPATCH — window.npDispatch(): orders placed before 4pm UK time on a
     working day go out the same day; otherwise on the next working day.
     Same rules and bank holidays as the live product page's delivery
     estimate (sections/main-product-page.liquid). Used by the home page
     dispatch line and the product page arrival line.
     Returns { sameDay, minsLeft, date, label, addWorkingDays }.
     ================================================================ */
  (function () {
    var CUTOFF = 16 * 60;
    /* England and Wales bank holidays (extend after 2027) */
    var BANK_HOLIDAYS = ",2026-01-01,2026-04-03,2026-04-06,2026-05-04,2026-05-25,2026-08-31,2026-12-25,2026-12-28," +
      "2027-01-01,2027-03-26,2027-03-29,2027-05-03,2027-05-31,2027-08-30,2027-12-27,2027-12-28,";
    function key(d) { return d.getFullYear() + "-" + ("0" + (d.getMonth() + 1)).slice(-2) + "-" + ("0" + d.getDate()).slice(-2); }
    function isWorkingDay(d) { var w = d.getDay(); return w !== 0 && w !== 6 && BANK_HOLIDAYS.indexOf("," + key(d) + ",") < 0; }
    function addWorkingDays(date, n) {
      var d = new Date(date);
      while (n > 0) { d.setDate(d.getDate() + 1); if (isWorkingDay(d)) n--; }
      return d;
    }
    /* the UK wall clock, whatever the shopper's own time zone */
    function ukNow() {
      var p = {};
      new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", hourCycle: "h23" })
        .formatToParts(new Date()).forEach(function (x) { p[x.type] = parseInt(x.value, 10); });
      return { day: new Date(p.year, p.month - 1, p.day), mins: (p.hour % 24) * 60 + p.minute };
    }
    window.npDispatch = function () {
      var now = ukNow();
      var sameDay = isWorkingDay(now.day) && now.mins < CUTOFF;
      var date = new Date(now.day);
      if (!sameDay) do { date.setDate(date.getDate() + 1); } while (!isWorkingDay(date));
      var days = Math.round((date - now.day) / 864e5);
      return {
        sameDay: sameDay,
        minsLeft: sameDay ? CUTOFF - now.mins : 0,
        date: date,
        label: days === 0 ? "today" : days === 1 ? "tomorrow" : date.toLocaleDateString("en-GB", { weekday: "long" }),
        addWorkingDays: addWorkingDays
      };
    };
  })();

  /* ================================================================
     LIVE CHAT CLOSE — a small "hide" button above the Shopify Inbox bubble
     (<shopify-chat>, app embed). It steps aside while the chat is open;
     pressing it hides the bubble for the rest of the visit (sessionStorage),
     and the bubble is back on the next visit. Styles: np-chrome.css.
     ================================================================ */
  (function () {
    var KEY = "np-chat-off";
    var root = document.documentElement;
    if (document.querySelector('link[href*="np-volt-skin"]')) return;   /* VOLT pages stay as they are */
    try { if (sessionStorage.getItem(KEY) === "1") { root.classList.add("np-chat-off"); return; } } catch (e) {}
    var btn = null, last = null;
    /* the bubble lives in the widget's shadow root (one level deeper once the chat has been opened) */
    function findBubble(node, depth) {
      if (!node || depth > 3) return null;
      var hit = node.querySelector('button.activator, [part~="activator"]');
      if (hit) return hit;
      var all = node.querySelectorAll("*");
      for (var i = 0; i < all.length; i++) {
        if (all[i].shadowRoot) { hit = findBubble(all[i].shadowRoot, depth + 1); if (hit) return hit; }
      }
      return null;
    }
    function place() {
      var host = document.querySelector("shopify-chat");
      if (!btn || !host) return;
      var bubble = findBubble(host.shadowRoot, 0);
      var r = bubble && bubble.getBoundingClientRect();
      if (r && r.width) last = { right: root.clientWidth - (r.left + r.width / 2), bottom: root.clientHeight - r.top };
      btn.hidden = host.hasAttribute("open") || !last || getComputedStyle(host).display === "none";
      if (btn.hidden) return;
      btn.style.right = (last.right - 14) + "px";   /* centred over the bubble (button 28px) */
      btn.style.bottom = (last.bottom + 8) + "px";
    }
    function setup(host) {
      btn = document.createElement("button");
      btn.type = "button";
      btn.className = "np-chat-x";
      btn.setAttribute("aria-label", "Hide live chat");
      btn.innerHTML = '<svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
      btn.addEventListener("click", function () {
        try { sessionStorage.setItem(KEY, "1"); } catch (e) {}
        root.classList.add("np-chat-off");
        btn.remove();
        btn = null;
      });
      document.body.appendChild(btn);
      new MutationObserver(place).observe(host, { attributes: true });
      window.addEventListener("resize", place);
      place();
    }
    /* the app embed adds the widget after load: look for it for up to 20s */
    var tries = 0;
    (function wait() {
      var host = document.querySelector("shopify-chat");
      if (host && findBubble(host.shadowRoot, 0)) { setup(host); return; }
      if (++tries < 40) setTimeout(wait, 500);
    })();
  })();

  /* ================================================================
     BACK TO TOP — a yellow button, bottom right (clear of the sticky
     bars), on pages longer than three screens once you are a screen and
     a half down (Adam, 7 Oct 2026).
     Styles: np-chrome.css.
     ================================================================ */
  (function () {
    if (document.querySelector('link[href*="np-volt-skin"]')) return;   /* VOLT pages stay as they are */
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "np-top";
    btn.setAttribute("aria-label", "Back to top");
    btn.tabIndex = -1;
    document.body.appendChild(btn);
    var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var queued = false;
    function update() {
      queued = false;
      var on = document.documentElement.scrollHeight > window.innerHeight * 3 && window.scrollY > window.innerHeight * 1.5;
      btn.classList.toggle("is-on", on);
      btn.tabIndex = on ? 0 : -1;
    }
    window.addEventListener("scroll", function () { if (!queued) { queued = true; requestAnimationFrame(update); } }, { passive: true });
    window.addEventListener("resize", update);
    btn.addEventListener("click", function () {
      window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
      /* keyboard users land back at the top of the page too */
      var first = document.querySelector("#np-dh-header a, header a");
      if (first) first.focus({ preventScroll: true });
    });
    update();
  })();

  /* ================================================================
     HEADER: burger drawer, mega menu (hover + keyboard), sticky-safe
     mega panel, header cart count.
     ================================================================ */
  (function () {
    var dhCart = $("np-dh-cart");
    var hdr = $("np-dh-header");

    /* header cart count follows np:cart (cart:change is blocked by the
       layout's own capture-phase guard for non-theme events) */
    if (dhCart) {
      document.documentElement.addEventListener("np:cart", function (e) {
        var c = e.detail && e.detail.cart;
        if (c) dhCart.textContent = "Cart · " + itemCount(c);
      });
      dhCart.addEventListener("click", function () {
        if (window.npSlideCart) window.npSlideCart.open();
        else window.location.href = ROOT + "cart";
      });
    }

    /* ---- mobile drawer ---- */
    var burger = $("np-dh-burger");
    var menu = $("np-dh-menu");
    var scrim = $("np-dh-scrim");
    if (burger && menu && scrim) {
      var drawer = new window.npChromeUtil.Dialog(menu, scrim, {
        onOpen: function () { burger.classList.add("is-open"); burger.setAttribute("aria-expanded", "true"); },
        onClose: function () { burger.classList.remove("is-open"); burger.setAttribute("aria-expanded", "false"); }
      });
      burger.setAttribute("aria-expanded", "false");
      burger.setAttribute("aria-controls", "np-dh-menu");
      burger.addEventListener("click", function () { drawer.isOpen ? drawer.close() : drawer.open(); });
      var closeBtn = $("np-dh-menu-close");
      if (closeBtn) closeBtn.addEventListener("click", function () { drawer.close(); });
      menu.addEventListener("click", function (e) { if (e.target.closest("a")) drawer.close(); });
      /* the drawer's search field hands over to the live search panel (the
         same results as the header's search icon) on a tap or the first
         keystroke, carrying what was typed; focus alone never moves anyone,
         so tabbing through the menu still works. The form itself stays as
         the fallback when the search script is not running. */
      var mmForm = qs(menu, ".np-mm-search");
      var mmIn = mmForm && qs(mmForm, 'input[name="q"]');
      if (mmIn) {
        var toLive = function (e) {
          if (!window.npSearch) return;
          if (e && e.type === "submit") e.preventDefault();
          var v = mmIn.value;
          mmIn.value = "";
          drawer.close();
          window.npSearch.open(v || null);
        };
        mmIn.addEventListener("click", toLive);
        mmIn.addEventListener("input", toLive);
        mmForm.addEventListener("submit", toLive);
      }
      /* one accordion open at a time (fallback where <details name> is unsupported) */
      var accs = qsa(menu, ".np-mm-list details");
      accs.forEach(function (d) {
        d.addEventListener("toggle", function () {
          if (!d.open) return;
          accs.forEach(function (o) { if (o !== d && o.open) o.open = false; });
        });
      });
    }

    /* ---- desktop mega menu ---- */
    var mg = $("np-mg");
    var mgScrim = $("np-mg-scrim");
    if (hdr && mg && mgScrim) {
      var tops = qsa(hdr, ".np-mg-top");
      var panels = qsa(mg, ".np-mg-p");
      var desk = window.matchMedia("(min-width:1020px)");
      var active = null, openedAt = 0, tOpen = null, tClose = null, OPEN_DELAY = 120, CLOSE_DELAY = 220;

      var setMg = function (k) {
        clearTimeout(tOpen); clearTimeout(tClose);
        if (k === active) return;
        active = k;
        if (k) openedAt = Date.now();
        tops.forEach(function (t) { if (t.hasAttribute("data-mg")) t.setAttribute("aria-expanded", t.getAttribute("data-mg") === k ? "true" : "false"); });
        if (k) panels.forEach(function (p) { p.hidden = p.getAttribute("data-mg") !== k; });
        mg.classList.toggle("is-open", !!k);
        mgScrim.classList.toggle("is-open", !!k);
        mg.setAttribute("aria-hidden", k ? "false" : "true");
        if (k) {
          mg.style.maxHeight = Math.max(240, window.innerHeight - hdr.getBoundingClientRect().bottom) + "px";
          if (window.npSearch) window.npSearch.close();
          if (window.npRatings) window.npRatings.fill(mg);
        }
      };

      function panelFor(k) { return qs(mg, '.np-mg-p[data-mg="' + k + '"]'); }
      function firstPanelLink(k) { var p = panelFor(k); return p ? p.querySelector("a,button") : null; }

      tops.forEach(function (t) {
        var k = t.getAttribute("data-mg");
        if (!k) {
          t.addEventListener("mouseenter", function () { setMg(null); });
          t.addEventListener("focus", function () { setMg(null); });
          return;
        }
        t.addEventListener("mouseenter", function () {
          clearTimeout(tOpen); clearTimeout(tClose);
          if (active == null) tOpen = setTimeout(function () { setMg(k); }, OPEN_DELAY);
          else setMg(k);
        });
        t.addEventListener("focus", function () { if (!returningFocus) setMg(k); });
        /* a click (or tap) arrives after the hover/focus that already opened
           this panel; only treat it as "close" once the panel has been open
           a moment */
        t.addEventListener("click", function (e) {
          e.preventDefault();
          setMg(active === k && Date.now() - openedAt > 400 ? null : k);
        });
        /* WCAG 2.1.1: ArrowDown (or Tab, once open) moves focus into the
           panel instead of the next nav item */
        t.addEventListener("keydown", function (e) {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            if (active !== k) setMg(k);
            var first = firstPanelLink(k);
            if (first) first.focus();
          } else if (e.key === "Tab" && !e.shiftKey && active === k) {
            var first2 = firstPanelLink(k);
            if (first2) { e.preventDefault(); first2.focus(); }
          }
        });
      });

      /* panel images are lazy so they cost nothing on page load; the first
         time the pointer or focus reaches the header they start loading, so
         they are in by the time a panel opens instead of popping in after it */
      var warm = function () {
        warm = function () {};
        qsa(mg, 'img[loading="lazy"]').forEach(function (i) { i.loading = "eager"; });
      };
      hdr.addEventListener("mouseenter", function () { clearTimeout(tClose); warm(); });
      hdr.addEventListener("focusin", function () { warm(); });
      hdr.addEventListener("mouseleave", function () {
        clearTimeout(tOpen); clearTimeout(tClose);
        tClose = setTimeout(function () { setMg(null); }, CLOSE_DELAY);
      });
      mgScrim.addEventListener("mouseenter", function () { setMg(null); });
      mgScrim.addEventListener("click", function () { setMg(null); });
      var returningFocus = false;
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && active) {
          var t = qs(hdr, '.np-mg-top[data-mg="' + active + '"]');
          setMg(null);
          /* hand focus back to the item without its focus handler reopening
             the panel that Escape just closed */
          if (t && mg.contains(document.activeElement)) {
            returningFocus = true;
            t.focus();
            returningFocus = false;
          }
        }
      });
      /* tabbing out of the header, or into the search bar, closes the panel
         so focus is never stranded behind a closed one */
      document.addEventListener("focusin", function (e) {
        if (!active) return;
        if (!hdr.contains(e.target) || e.target.id === "nps-input") setMg(null);
      });
      var onBp = function () { if (!desk.matches) setMg(null); };
      if (desk.addEventListener) desk.addEventListener("change", onBp); else if (desk.addListener) desk.addListener(onBp);
    }
  })();

  /* ================================================================
     SEARCH
     ================================================================ */
  (function () {
    var cfg = readJSON("nps-config") || {};
    var SUGGEST = cfg.suggestUrl, SEARCH = cfg.searchUrl, SECTION = "np-predictive-search";
    var TERMS = (cfg.terms || []).slice();

    var mq = window.matchMedia("(max-width:1019px)");
    var panel = $("nps"), scrim = $("nps-scrim"), bar = $("nps-bar"), dIn = $("nps-input"), mIn = $("nps-minput");
    var mTrigger = $("nps-mtrigger");
    var clearBtn = $("nps-clear"), mClear = $("nps-mclear"), kbd = $("nps-kbd"), status = $("nps-status");
    var empty = $("nps-empty"), results = $("nps-results"), none = $("nps-none");
    var viewAll = $("nps-viewall"), mView = $("nps-mview"), scroller = $("nps-scroll");
    var hdr = $("np-dh-header");
    if (!panel || !bar || !dIn) return;

    var isOpen = false, query = "", ctrl = null, debounceT = null, skeletonT = null, active = -1, cache = {};
    var isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
    var DESK_PH = dIn.getAttribute("placeholder") || "Search";

    function mob() { return mq.matches; }
    function input() { return mob() ? mIn : dIn; }

    /* ---- recent searches (this browser only) ---- */
    var RECENT_KEY = "np-recent-searches";
    function getRecent() { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").filter(Boolean).slice(0, 5); } catch (e) { return []; } }
    function setRecent(list) { try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5))); } catch (e) {} }
    function addRecent(t) {
      t = String(t || "").trim().toLowerCase();
      if (!t) return;
      setRecent([t].concat(getRecent().filter(function (r) { return r !== t; })));
    }
    function renderRecent() {
      var list = getRecent(), block = $("nps-recentblock"), box = $("nps-recent");
      if (!block || !box) return;
      block.hidden = !list.length;
      box.innerHTML = list.map(function (r) {
        return '<span class="nps-rc"><button type="button" data-term="' + esc(r) + '">' + esc(r) + '</button><button type="button" data-recent-remove="' + esc(r) + '" aria-label="Remove ' + esc(r) + '">&times;</button></span>';
      }).join("");
    }

    /* ---- bar chrome ---- */
    function syncChrome() {
      var has = !!query;
      if (clearBtn) clearBtn.hidden = !has;
      if (mClear) mClear.hidden = !has;
      if (status) status.hidden = !has;
      if (kbd) { kbd.hidden = has; kbd.textContent = isOpen ? "Esc" : (isMac ? "⌘ K" : "Ctrl K"); }
    }
    function placePanel() {
      if (mob() || !hdr) return;
      var b = Math.max(0, Math.round(hdr.getBoundingClientRect().bottom));
      panel.style.setProperty("--nps-top", b + "px");
    }

    var searchHideFn = null, searchHideTimer = null, searchRaf = 0, lockedMob = false;
    function blockPageScroll(e) {
      if (!isOpen) return;
      var sc = $("nps-scroll");
      if (sc && sc.contains(e.target) && sc.scrollHeight > sc.clientHeight + 1) {
        if (e.type !== "wheel") return;
        var up = e.deltaY < 0;
        var atTop = sc.scrollTop <= 0, atEnd = sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 1;
        if (!(up && atTop) && !(!up && atEnd)) return;
      }
      e.preventDefault();
    }
    function cancelSearchHide() {
      clearTimeout(searchHideTimer);
      if (searchHideFn) { panel.removeEventListener("transitionend", searchHideFn); searchHideFn = null; }
    }
    function setOpen(on) {
      if (on === isOpen) return;
      isOpen = on;
      cancelSearchHide();
      if (on) {
        if (panel.style.display === "none") panel.style.display = "";
        /* visible now (still transparent until is-open lands next frame) so
           the input can take focus inside the tap or keystroke that opened
           the panel, instead of keystrokes landing on whatever had focus */
        panel.style.visibility = "visible";
        placePanel();
      } else {
        panel.style.visibility = "";
      }
      cancelAnimationFrame(searchRaf);
      searchRaf = requestAnimationFrame(function () {
        if (isOpen !== on) return;
        panel.classList.toggle("is-open", on);
        scrim.classList.toggle("is-open", on && !mob());
        bar.classList.toggle("is-open", on && !mob());
      });
      if (hdr) hdr.classList.toggle("nps-open", on && !mob());
      panel.setAttribute("aria-hidden", on ? "false" : "true");
      dIn.setAttribute("aria-expanded", on ? "true" : "false");
      dIn.setAttribute("placeholder", on && !mob() ? "Search products, guides, ingredients" : DESK_PH);
      /* Desktop: don't lock <html> overflow. With it locked, typing in the
         sticky header's input made the browser scroll the caret into view and
         jump the page to the top, leaving the panel half under the header.
         Block wheel/touch scrolling outside the results instead. Mobile keeps
         the overflow lock (its input sits inside the full-screen panel). */
      if (on) {
        lockedMob = mob();
        if (lockedMob) window.npChromeUtil.lockScroll(true);
        else { window.addEventListener("wheel", blockPageScroll, { passive: false }); window.addEventListener("touchmove", blockPageScroll, { passive: false }); }
      } else {
        if (lockedMob) window.npChromeUtil.lockScroll(false);
        window.removeEventListener("wheel", blockPageScroll, { passive: false });
        window.removeEventListener("touchmove", blockPageScroll, { passive: false });
        lockedMob = false;
      }
      if (on) {
        renderRecent();
        fillRatings(panel);
        mIn.value = query;
        if (mob()) setTimeout(function () { mIn.focus(); }, 60);
      } else {
        active = -1;
        markActive();
        busy(false);
        releaseHeight();
        if (document.activeElement === dIn || document.activeElement === mIn) document.activeElement.blur();
        searchHideFn = function (e) {
          if (e && e.target !== panel) return;
          cancelSearchHide();
          if (!isOpen) panel.style.display = "none";
        };
        panel.addEventListener("transitionend", searchHideFn);
        searchHideTimer = setTimeout(searchHideFn, 500);
      }
      syncChrome();
    }

    /* ---- states ----
       Smoothness rules: current state stays on screen while the next request
       runs and only dims if slow (>200ms); no skeleton unless there is
       nothing to show and the network is very slow (>900ms); "Searching"
       keeps its slot so the input never changes width mid-word; on desktop
       the panel holds its tallest height for the typing session. */
    var wrap = qs(panel, ".nps-wrap");
    var busyT = null, lockH = 0, painted = "", state = "empty";

    function holdHeight() {
      if (mob() || !wrap) return;
      var h = wrap.offsetHeight;
      if (h > lockH) { lockH = h; wrap.style.minHeight = lockH + "px"; }
    }
    function releaseHeight() { clearTimeout(settleT); lockH = 0; if (wrap) wrap.style.minHeight = ""; }

    var settleT = null, growT = null;
    function scheduleSettle() {
      clearTimeout(settleT);
      settleT = setTimeout(function () {
        if (mob() || !wrap || !lockH) return;
        var vis = [empty, results, none].filter(function (e) { return !e.hidden; })[0];
        if (!vis) return;
        var cs = getComputedStyle(wrap);
        var natural = Math.ceil(vis.offsetHeight + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom));
        if (natural >= lockH - 2) return;
        lockH = natural;
        wrap.style.minHeight = natural + "px";
      }, 700);
    }

    function busy(on) {
      clearTimeout(busyT);
      if (on) {
        busyT = setTimeout(function () { panel.classList.add("is-busy"); if (status) status.classList.add("is-on"); }, 200);
      } else {
        panel.classList.remove("is-busy");
        if (status) status.classList.remove("is-on");
      }
    }

    function show(next) {
      if (next !== state) scroller.scrollTop = 0;
      state = next;
      empty.hidden = next !== "empty";
      results.hidden = next !== "results" && next !== "loading";
      none.hidden = next !== "none";
      var va = next === "results";
      viewAll.hidden = !va;
      panel.classList.toggle("has-viewall", va);
      if (va) {
        var href = SEARCH + "?q=" + encodeURIComponent(query.trim()) + "&type=product&options%5Bprefix%5D=last";
        viewAll.href = href;
        mView.href = href;
        viewAll.innerHTML = "View all results for “" + esc(query.trim()) + "” &rarr;";
        mView.innerHTML = "View all results &rarr;";
      }
    }

    function skeleton() {
      var card = '<div class="nps-skcard nps-sk"><i></i><span><b></b><u></u></span></div>';
      var row = '<div class="nps-skrow nps-sk"><i></i><span><b></b><u></u></span></div>';
      painted = "";
      results.innerHTML =
        '<div class="nps-cols"><div class="nps-left"></div><div class="nps-right"><div class="nps-h">Products</div>' +
        '<div class="nps-grid nps-only-desk">' + card + card + card + card + '</div>' +
        '<div class="nps-list nps-only-mob">' + row + row + row + "</div></div></div>";
      panel.classList.remove("is-busy");
      show("loading");
    }

    function highlightIn(root) {
      var q = query.trim().toLowerCase();
      if (!q) return;
      qsa(root, "[data-hl]").forEach(function (el) {
        var t = el.textContent, i = t.toLowerCase().indexOf(q);
        if (i < 0) return;
        el.innerHTML = esc(t.slice(0, i)) + '<span class="nps-hit">' + esc(t.slice(i, i + q.length)) + "</span>" + esc(t.slice(i + q.length));
      });
    }

    /* only the layout the current viewport actually shows should fetch
       images eagerly — done here, on the still-detached fragment, before
       it is ever connected to the document (eager images fetch the instant
       they are connected, regardless of CSS display) */
    function fixEagerLayout(root) {
      var hideMob = !mob();
      qsa(root, (hideMob ? ".nps-only-mob" : ".nps-only-desk") + ' img[loading="eager"]').forEach(function (img) {
        img.loading = "lazy";
      });
    }

    /* ---- ratings ---- */
    var RATE_KEY = "np-ls-ratings", rates = {}, probeBox = null, probeWait = 0;
    try { rates = JSON.parse(sessionStorage.getItem(RATE_KEY) || "{}") || {}; } catch (e) { rates = {}; }
    function saveRates() { try { sessionStorage.setItem(RATE_KEY, JSON.stringify(rates)); } catch (e) {} }
    function rateHTML(v) { return v && v[1] ? '<span class="nps-star">&#9733;</span><b>' + v[0].toFixed(1) + "</b><span>(" + v[1] + ")</span>" : ""; }
    function putRate(el, v, fade) {
      el.setAttribute("data-rated", "");
      el.innerHTML = rateHTML(v);
      if (fade && el.innerHTML) el.classList.add("is-in");
    }
    function lsReady() { return !!(window.lipscore && typeof lipscore.initWidgets === "function" && (typeof lipscore.isInitialized !== "function" || lipscore.isInitialized())); }
    function gotRate(id, r10, votes) {
      rates[id] = votes > 0 && r10 > 0 ? [Math.round(r10 / 2 * 10) / 10, votes] : 0;
      saveRates();
      document.querySelectorAll('[data-rate="' + id + '"]:not([data-rated])').forEach(function (el) { putRate(el, rates[id], true); });
    }
    function fillRatings(root) {
      var need = [], meta = {};
      qsa(root, "[data-rate]:not([data-rated])").forEach(function (el) {
        var id = el.getAttribute("data-rate");
        if (Object.prototype.hasOwnProperty.call(rates, id)) putRate(el, rates[id], false);
        else if (!meta[id]) { need.push(id); meta[id] = el; }
      });
      if (!need.length) return;
      if (!lsReady()) {
        if (probeWait++ < 20) setTimeout(function () { fillRatings(document.documentElement.contains(root) ? root : panel); }, 300);
        return;
      }
      if (!probeBox) {
        probeBox = document.createElement("div");
        probeBox.hidden = true;
        probeBox.setAttribute("aria-hidden", "true");
        document.body.appendChild(probeBox);
        new MutationObserver(function (muts) {
          muts.forEach(function (m) {
            var s = m.target;
            if (!s.getAttribute || !s.hasAttribute("data-ls-product-votes") || !s.parentNode) return;
            gotRate(s.getAttribute("data-ls-product-id"), parseFloat(s.getAttribute("data-ls-product-rating")) || 0, parseInt(s.getAttribute("data-ls-product-votes"), 10) || 0);
            s.parentNode.removeChild(s);
          });
        }).observe(probeBox, { attributes: true, subtree: true, attributeFilter: ["data-ls-product-votes"] });
      }
      need.forEach(function (id) {
        if (probeBox.querySelector('[data-ls-product-id="' + id + '"]')) return;
        var s = document.createElement("span");
        s.className = "lipscore-rating-small";
        s.setAttribute("data-ls-product-id", id);
        s.setAttribute("data-ls-product-name", meta[id].getAttribute("data-rate-name") || "");
        s.setAttribute("data-ls-brand", "EFECTIV Nutrition");
        s.setAttribute("data-ls-product-url", location.origin + (meta[id].getAttribute("data-rate-url") || ""));
        probeBox.appendChild(s);
        setTimeout(function () {
          if (!s.parentNode) return;
          s.parentNode.removeChild(s);
          if (!Object.prototype.hasOwnProperty.call(rates, id)) { rates[id] = 0; saveRates(); }
        }, 6000);
      });
      try { lipscore.initWidgets(); } catch (e) {}
    }

    function lev(a, b) {
      var m = a.length, n = b.length, d = [], i, j;
      for (i = 0; i <= m; i++) d[i] = [i];
      for (j = 1; j <= n; j++) d[0][j] = j;
      for (i = 1; i <= m; i++) for (j = 1; j <= n; j++)
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      return d[m][n];
    }
    function didYouMean(q) {
      var best = 99, pick = "";
      TERMS.forEach(function (t) {
        t.split(/[^a-z]+/).concat([t]).forEach(function (c) {
          if (c.length < 3) return;
          var d = lev(q, c);
          if (d < best && d <= Math.max(2, Math.floor(c.length / 4))) { best = d; pick = c; }
        });
      });
      return pick !== q ? pick : "";
    }
    /* the term list embedded on every page load is just the cheap trending +
       category chips; a fuller product-name list only ever arrives inside a
       genuine zero-result predictive-search response (data-terms), so it
       costs nothing on pages where search is never opened */
    function mergeTerms(csv) {
      if (!csv) return;
      csv.split("|").forEach(function (t) {
        t = t.trim();
        if (t && TERMS.indexOf(t) === -1) TERMS.push(t);
      });
    }

    function paint(html) {
      busy(false);
      var q = query.trim();
      var stamp = q.toLowerCase() + "\n" + html;
      if (stamp === painted && (state === "results" || state === "none")) return;
      holdHeight();
      var fromH = (!mob() && wrap) ? wrap.offsetHeight : 0;
      var tmp = document.createElement("div");
      tmp.innerHTML = html;
      fixEagerLayout(tmp);
      var r = tmp.querySelector("#nps-r");
      var n = function (k) { return r ? Number(r.getAttribute("data-" + k)) || 0 : 0; };
      var total = n("products") + n("collections") + n("articles") + n("queries");
      active = -1;
      if (!total) {
        if (r) mergeTerms(r.getAttribute("data-terms"));
        $("nps-none-q").textContent = q;
        var dym = didYouMean(q.toLowerCase());
        $("nps-dym").hidden = !dym;
        $("nps-dym-term").textContent = dym;
        $("nps-dym").setAttribute("data-term", dym);
        show("none");
      } else {
        highlightIn(tmp);
        fillRatings(tmp);
        var frag = document.createDocumentFragment();
        while (tmp.firstChild) frag.appendChild(tmp.firstChild);
        results.replaceChildren ? results.replaceChildren(frag) : (results.innerHTML = "", results.appendChild(frag));
        show("results");
      }
      painted = stamp;
      if (!mob() && wrap) {
        var toH = wrap.offsetHeight;
        if (toH > fromH + 4) {
          clearTimeout(growT);
          wrap.style.height = fromH + "px";
          wrap.style.overflow = "hidden";
          void wrap.offsetHeight;
          wrap.style.transition = "opacity .2s ease, min-height .34s cubic-bezier(.16,1,.3,1), height .34s cubic-bezier(.16,1,.3,1)";
          wrap.style.height = toH + "px";
          growT = setTimeout(function () { wrap.style.height = ""; wrap.style.overflow = ""; wrap.style.transition = ""; }, 380);
        }
        if (toH > lockH) { lockH = toH; wrap.style.minHeight = lockH + "px"; }
      }
      scheduleSettle();
    }

    var EMPTY_R = '<div id="nps-r" data-products="0" data-collections="0" data-articles="0" data-queries="0"></div>';

    function run(q) {
      q = q.trim();
      if (!q) { busy(false); show("empty"); return; }
      var key = q.toLowerCase();
      if (cache[key]) { clearTimeout(skeletonT); paint(cache[key]); return; }
      if (ctrl) ctrl.abort();
      ctrl = window.AbortController ? new AbortController() : null;
      busy(true);
      clearTimeout(skeletonT);
      if (state === "empty" || state === "loading") skeletonT = setTimeout(skeleton, 900);
      var url = SUGGEST + "?q=" + encodeURIComponent(q) +
        "&resources%5Btype%5D=query,product,collection,article&resources%5Blimit%5D=6&resources%5Blimit_scope%5D=each" +
        "&resources%5Boptions%5D%5Bunavailable_products%5D=last&section_id=" + SECTION;
      var opts = ctrl ? { signal: ctrl.signal } : {};
      /* theme preview only: the browser's preview session can lapse while a page is open; the request then reaches
         the published theme, which has no np-predictive-search section (404). Renew the session once and ask again
         (preview_theme_id cannot ride on this request: Shopify's redirect mangles the resources[] params). Never
         runs on the published theme. (Adam, 9 Oct 2026: typing showed "Nothing for" every term) */
      var preview = window.Shopify && Shopify.theme && Shopify.theme.role && Shopify.theme.role !== "main" ? Shopify.theme.id : null;
      function get(retried) {
        return fetch(url, opts).then(function (res) {
          if (res.ok) return res.text();
          if (preview && !retried && res.status === 404) {
            return fetch(ROOT + "?preview_theme_id=" + preview, opts).then(function () { return get(true); });
          }
          return Promise.reject(new Error("bad response"));
        });
      }
      get(false)
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, "text/html");
          var r = doc.getElementById("nps-r");
          var htmlOut = r ? r.outerHTML : EMPTY_R;
          cache[key] = htmlOut;
          if (query.trim().toLowerCase() === key) { clearTimeout(skeletonT); paint(htmlOut); }
        })
        .catch(function (e) {
          if (e && e.name === "AbortError") return;
          /* never cache an error, and never call a failed request "no results":
             show the default panel instead (Enter still opens the full results) */
          if (query.trim().toLowerCase() === key) { clearTimeout(skeletonT); busy(false); show("empty"); }
        });
    }

    function setQuery(v, now) {
      var was = query.trim();
      query = v;
      if (dIn.value !== v) dIn.value = v;
      if (mIn.value !== v) mIn.value = v;
      syncChrome();
      clearTimeout(debounceT);
      clearTimeout(settleT);
      if (!v.trim()) {
        if (ctrl) ctrl.abort();
        clearTimeout(skeletonT);
        busy(false);
        releaseHeight();
        painted = "";
        show("empty");
        return;
      }
      if (now || !was) run(v); else debounceT = setTimeout(function () { run(v); }, 200);
    }

    function useTerm(t) {
      if (!t) return;
      addRecent(t);
      setOpen(true);
      setQuery(t, true);
      var el = input();
      if (!mob()) el.focus();
    }

    function goSearch(t) {
      t = String(t || "").trim();
      if (!t) return;
      addRecent(t);
      window.location.href = SEARCH + "?q=" + encodeURIComponent(t) + "&type=product&options%5Bprefix%5D=last";
    }

    function suggs() { return results.hidden ? [] : qsa(results, ".nps-s"); }
    function markActive() {
      suggs().forEach(function (b, i) { b.classList.toggle("is-active", i === active); b.setAttribute("aria-selected", i === active ? "true" : "false"); });
    }

    /* ---- wiring ---- */
    dIn.addEventListener("focus", function () { if (!mob()) setOpen(true); });
    dIn.addEventListener("input", function () { setQuery(dIn.value); });
    mIn.addEventListener("input", function () { setQuery(mIn.value); });
    if (mTrigger) {
      /* below 1020px the trigger is a real, focusable, labelled button
         rather than a form styled to look clickable */
      mTrigger.addEventListener("click", function () { setOpen(true); });
    }
    bar.addEventListener("click", function (e) {
      if (mob()) { e.preventDefault(); setOpen(true); return; }
      if (e.target !== clearBtn) dIn.focus();
    });
    bar.addEventListener("submit", function (e) {
      e.preventDefault();
      if (mob()) { setOpen(true); return; }
      var list = suggs();
      if (active >= 0 && list[active]) useTerm(list[active].getAttribute("data-term"));
      else goSearch(query);
    });
    dIn.addEventListener("keydown", function (e) {
      var list = suggs(), n = list.length;
      if (e.key === "ArrowDown" && n) { e.preventDefault(); active = (active + 1) % n; markActive(); }
      else if (e.key === "ArrowUp" && n) { e.preventDefault(); active = (active - 1 + n) % n; markActive(); }
    });
    /* Enter (the keyboard's search key) opens the full results page, as on
       desktop; tapping a product or a suggestion stays in the panel */
    mIn.addEventListener("keydown", function (e) {
      if (e.key === "Enter") { e.preventDefault(); if (query.trim()) goSearch(query); else mIn.blur(); }
    });
    if (clearBtn) clearBtn.addEventListener("click", function (e) { e.preventDefault(); e.stopPropagation(); setQuery(""); dIn.focus(); });
    if (mClear) mClear.addEventListener("click", function (e) { e.preventDefault(); setQuery(""); mIn.focus(); });
    var cancelBtn = $("nps-cancel");
    if (cancelBtn) cancelBtn.addEventListener("click", function () { setQuery(""); setOpen(false); });
    var clearRecentBtn = $("nps-clearrecent");
    if (clearRecentBtn) clearRecentBtn.addEventListener("click", function () { setRecent([]); renderRecent(); });
    scrim.addEventListener("click", function () { setOpen(false); });

    panel.addEventListener("click", function (e) {
      var rm = e.target.closest("[data-recent-remove]");
      if (rm) { var v = rm.getAttribute("data-recent-remove"); setRecent(getRecent().filter(function (r) { return r !== v; })); renderRecent(); return; }
      var fill = e.target.closest("[data-fill]");
      if (fill) { setQuery(fill.getAttribute("data-fill") + " "); mIn.focus(); return; }
      var term = e.target.closest("[data-term]");
      if (term) { useTerm(term.getAttribute("data-term")); return; }
      if (e.target.closest("[data-track]") && query.trim()) addRecent(query);
    });
    panel.addEventListener("mouseover", function (e) {
      var b = e.target.closest(".nps-s");
      if (!b) return;
      var i = suggs().indexOf(b);
      if (i !== active) { active = i; markActive(); }
    });

    document.addEventListener("keydown", function (e) {
      var k = e.key;
      if ((e.metaKey || e.ctrlKey) && (k === "k" || k === "K")) {
        e.preventDefault();
        if (isOpen && !mob()) { setOpen(false); return; }
        setOpen(true);
        input().focus();
        return;
      }
      if (k === "/" && !isOpen && !/^(INPUT|TEXTAREA|SELECT)$/.test((document.activeElement || {}).tagName || "") && !(document.activeElement && document.activeElement.isContentEditable)) {
        e.preventDefault();
        setOpen(true);
        input().focus();
        return;
      }
      if (k === "Escape" && isOpen) { setOpen(false); }
    });

    window.addEventListener("resize", function () { if (isOpen) placePanel(); });
    if (hdr && window.ResizeObserver) new ResizeObserver(function () { if (isOpen) placePanel(); }).observe(hdr);
    if (mq.addEventListener) mq.addEventListener("change", function () { if (isOpen) setOpen(false); });

    window.npSearch = { open: function (q) { setOpen(true); if (q != null) setQuery(String(q), true); input().focus(); }, close: function () { setOpen(false); } };
    window.npRatings = { fill: fillRatings };
    syncChrome();
  })();

  /* ================================================================
     SLIDE CART
     ================================================================ */
  (function () {
    var panel = $("npc");
    if (!panel) return;
    var cfg = readJSON("npc-config") || {};
    var GOAL = cfg.shipGoal || 3000;
    var FORMAT = cfg.moneyFormat || "£{{amount}}";
    var UPSELLS = cfg.upsells || [];

    var scrim = $("npc-scrim");
    var scroll = $("npc-scroll");
    var sums = $("npc-sums");
    var countEl = $("npc-count");
    var shipMsg = $("npc-shipmsg");
    var bar = $("npc-bar");
    var ctaTotal = $("npc-cta-total");
    var foot = $("npc-foot");
    var planEl = null, planCache = {}, plansLoaded = false;
    var cart = cfg.cart || { items: [], item_count: 0, total_price: 0, items_subtotal_price: 0 };
    if (window.npCart) window.npCart.cart = function () { return cart; };   /* read-only view for other sections (the home sticky bar seeds its count from it) */
    var desired = {};       /* line key -> quantity the user actually wants */
    var packWant = {};      /* pack line key -> quantity wanted; a pack is written in one request with its components (changePack) */
    var codeOpen = false, codeNote = "";
    var rendered = false;   /* the heavy DOM (cart lines) only builds on first open */

    /* ---- one serial queue for every write the cart makes (quantity change,
       removal, upsell add, plan change, discount) ---- */
    var queue = Promise.resolve();
    function enqueue(task) {
      queue = queue.then(task, task);
      return queue;
    }
    function setBusy(on) { panel.classList.toggle("npc-busy", on); }

    function dispatchCartEvent() {
      document.documentElement.dispatchEvent(new CustomEvent("np:cart", { bubbles: true, detail: { cart: cart } }));
    }

    function variantLine(item) {
      var bits = [];
      if (item.options_with_values) {
        for (var i = 0; i < item.options_with_values.length; i++) {
          var o = item.options_with_values[i];
          if (o.value && String(o.value).toLowerCase() !== "default title") bits.push(o.value);
        }
      }
      if (!bits.length && item.variant_title && item.variant_title !== "Default Title") bits.push(item.variant_title);
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) bits.push(item.selling_plan_allocation.selling_plan.name);
      return bits.join(" · ");
    }

    /* ---- packs in the slide cart (isComp, isPack, ownedComps, itemCount, packRoom: shared utilities at the top of this file) ---- */
    /* codes that exist only for a pack: every pound they save sits on component lines. Lower-case code -> those line keys */
    function packCodes(items) {
      var seen = {}, out = {};
      items.forEach(function (it) {
        (it.line_level_discount_allocations || []).forEach(function (a) {
          var d = a.discount_application;
          if (!d || d.type !== "discount_code" || !(a.amount > 0)) return;
          var s = seen[String(d.title).toLowerCase()] || (seen[String(d.title).toLowerCase()] = { keys: [], other: false });
          if (isComp(it)) s.keys.push(it.key); else s.other = true;
        });
      });
      Object.keys(seen).forEach(function (k) { if (seen[k].keys.length && !seen[k].other) out[k] = seen[k].keys; });
      return out;
    }
    /* one cart/update.js body that moves a pack to qty together with its components, and drops a pack code once no component is left */
    function packUpdate(fresh, key, qty) {
      var items = fresh.items || [];
      var pack = items.filter(function (i) { return i.key === key; })[0];
      if (!pack || !isPack(pack)) return null;
      /* never above the order limit, whatever asked (12 Oct 2026) */
      if (qty > pack.quantity) qty = pack.quantity + Math.max(0, Math.min(qty - pack.quantity, packRoom(items, pack)));
      var sets = pack.quantity - qty;
      var updates = {};
      updates[key] = qty;
      /* a component can sit on two lines once the pack's code runs out of uses (cart.js splits them): take the change off the variant once, not once per line */
      var rest = {};
      ownedComps(items, pack).forEach(function (c) {
        var left = Object.prototype.hasOwnProperty.call(rest, c.variant_id) ? rest[c.variant_id] : sets;
        var take = left > 0 ? Math.min(c.quantity, left) : left;
        updates[c.key] = c.quantity - take;
        rest[c.variant_id] = left - take;
      });
      function alive(k) { return Object.prototype.hasOwnProperty.call(updates, k) ? updates[k] > 0 : true; }
      var carriers = packCodes(items);
      var keep = (fresh.discount_codes || []).filter(function (d) {
        var ks = carriers[String(d.code).toLowerCase()];
        return !ks || ks.some(alive);
      }).map(function (d) { return d.code; });
      return { updates: updates, discount: keep.join(",") };
    }

    function recomputeTotals() {
      var items = cart.items || [];
      cart.item_count = items.reduce(function (n, i) { return n + i.quantity; }, 0);
      cart.items_subtotal_price = items.reduce(function (n, i) { return n + i.final_line_price; }, 0);
      var off = (cart.cart_level_discount_applications || []).reduce(function (n, d) { return n + (d.total_allocated_amount || 0); }, 0);
      cart.total_price = Math.max(0, cart.items_subtotal_price - off);
    }

    /* after every server reply, unconfirmed taps are re-applied on top of the
       fresh authoritative cart so a slower response never regresses what the
       shopper is currently looking at */
    function applyDesiredOverrides() {
      var items = (cart.items || []).slice();
      items.forEach(function (it) {
        if (!Object.prototype.hasOwnProperty.call(desired, it.key)) return;
        var q = desired[it.key];
        if (q > 0) {
          var unit = it.quantity ? it.final_line_price / it.quantity : it.price;
          var was = it.quantity ? it.original_line_price / it.quantity : it.price;
          it.quantity = q;
          it.final_line_price = Math.round(unit * q);
          it.original_line_price = Math.round(was * q);
        }
      });
      cart.items = items.filter(function (it) { return !(Object.prototype.hasOwnProperty.call(desired, it.key) && desired[it.key] <= 0); });
      recomputeTotals();
    }

    function renderTotals() {
      var items = cart.items || [];
      var count = itemCount(cart);   /* a pack counts as one item (12 Oct 2026) */
      if (countEl) countEl.textContent = count === 1 ? "1 item" : count + " items";
      var dhCart = $("np-dh-cart");
      if (dhCart) dhCart.textContent = "Cart · " + count;

      var payable = cart.total_price;
      var left = Math.max(0, GOAL - payable);
      if (shipMsg) {
        shipMsg.textContent = !items.length
          ? "Free UK delivery on orders over " + money(GOAL, FORMAT) + "."
          : (left <= 0 ? "You've unlocked free UK delivery." : "You're " + money(left, FORMAT) + " away from free UK delivery.");
      }
      if (bar) bar.style.transform = "scaleX(" + Math.min(1, GOAL ? payable / GOAL : 0) + ")";
      if (!sums) return;
      if (!items.length) { sums.innerHTML = ""; return; }

      var rows = '<div class="npc-sum"><span>Subtotal</span><span>' + money(cart.items_subtotal_price, FORMAT) + "</span></div>";
      (cart.cart_level_discount_applications || []).forEach(function (d) {
        rows += '<div class="npc-sum npc-sum--on"><span>' + esc(d.title) + "</span><span>&minus;" + money(d.total_allocated_amount, FORMAT) + "</span></div>";
      });
      rows += '<div class="npc-sum"><span>Delivery</span><span>' + (cart.total_price >= GOAL ? "Free" : "Calculated at checkout") + "</span></div>";
      rows += '<div class="npc-total"><span>Total</span><span>' + money(cart.total_price, FORMAT) + "</span></div>";
      sums.innerHTML = rows;
      if (ctaTotal) ctaTotal.textContent = money(cart.total_price, FORMAT);
    }

    function patchRow(key) {
      var it = (cart.items || []).filter(function (i) { return i.key === key; })[0];
      var row = qs(scroll, '[data-key="' + key + '"]');
      if (!it || !row) return false;
      row.querySelector(".npc-qty span").textContent = it.quantity;
      row.querySelector(".npc-line").textContent = money(it.final_line_price, FORMAT);
      var wasEl = row.querySelector(".npc-was");
      if (it.original_line_price > it.final_line_price) {
        if (wasEl) wasEl.textContent = money(it.original_line_price, FORMAT);
        else row.querySelector(".npc-prices").insertAdjacentHTML("afterbegin", '<span class="npc-was">' + money(it.original_line_price, FORMAT) + "</span>");
      } else if (wasEl) wasEl.remove();
      renderTotals();
      return true;
    }

    /* the lines a pack includes: name and choice, "Included" where the price would be, nothing to press.
       The count is the pack's own (two packs with the same shaker share one component line). */
    function includedList(comps, sets) {
      var h = '<ul class="npc-incl" aria-label="Included in this pack">';
      comps.forEach(function (c) {
        var v = variantLine(c);
        h += '<li class="npc-inclrow"><span class="npc-inclt"><b>' + (sets > 1 ? sets + " &times; " : "") + esc(shortName(c.product_title)) + "</b>" +
          (v ? "<small>" + esc(v) + "</small>" : "") + '</span><span class="npc-inclp">Included</span></li>';
      });
      return h + "</ul>";
    }

    /* every code on the cart, applied or not valid, each with a remove button (a pack's own code is part of the pack, so it is not listed) */
    function codeChips() {
      var own = packCodes(cart.items || []), h = "";
      (cart.discount_codes || []).forEach(function (d) {
        if (own[String(d.code).toLowerCase()]) return;
        var name = String(d.code).toUpperCase();
        h += '<li class="npc-chip' + (d.applicable ? "" : " is-bad") + '"><span class="npc-chipt"><b>' + esc(name) + "</b><small>" +
          (d.applicable ? "Applied" : "Not valid for this cart") + "</small></span>" +
          '<button type="button" class="npc-chipx" data-act="code-remove" data-code="' + esc(d.code) + '" aria-label="Remove code ' + esc(name) + '">' +
          '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2 2l8 8M10 2l-8 8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button></li>';
      });
      return h;
    }

    function render() {
      if (!scroll) return;
      rendered = true;
      var items = cart.items || [];
      renderTotals();

      if (!items.length) {
        scroll.innerHTML =
          '<div class="npc-empty"><h3>Your cart is empty</h3>' +
          "<p>Take the 60-second quiz and we'll build a stack around your training.</p>" +
          '<div class="npc-emptyctas">' +
          '<a class="npc-emptycta" href="/collections/all-products">Shop all products</a>' +
          '<a class="npc-emptycta npc-emptycta--line" href="/pages/quiz">Take the quiz</a>' +
          "</div></div>";
        if (foot) foot.style.display = "none";
        return;
      }
      if (foot) foot.style.display = "";

      /* a pack's component lines are listed inside the pack's card, "Included", with no controls (a component with no pack in the cart is just a line) */
      var kids = {}, claimed = {};
      items.forEach(function (it) {
        if (!isPack(it)) return;
        kids[it.key] = ownedComps(items, it);
        kids[it.key].forEach(function (c) { claimed[c.key] = true; });
      });

      var html = '<div class="npc-items">';
      var n = 0;
      items.forEach(function (it) {
        if (claimed[it.key]) return;
        n++;
        var packed = kids[it.key] && kids[it.key].length;
        var wanted = Object.prototype.hasOwnProperty.call(packWant, it.key);
        var atMax = isPack(it) && packRoom(items, it) <= 0;   /* the order already holds the most packs the code covers */
        var was = it.original_line_price > it.final_line_price ? '<span class="npc-was">' + money(it.original_line_price, FORMAT) + "</span>" : "";
        var sub = variantLine(it);
        /* test theme: black web render for this product/flavour when there is one (cfg.bk, snippets/np-bk.liquid) */
        var bkm = cfg.bk && cfg.bk[it.handle], bkf = "*";
        (it.options_with_values || []).forEach(function (o) { if (o.name === "Flavour") bkf = o.value; });
        var thumb = bkm && (bkm[bkf] || bkm["*"]);
        if (!thumb && it.image) thumb = it.image.replace(/(\.[a-z]+)(\?|$)/i, "_180x$1$2");
        html +=
          '<div class="npc-item' + (packed ? " has-incl" : "") + (wanted ? " is-busy" : "") + '" data-key="' + esc(it.key) + '">' +
          '<a class="npc-thumb" href="' + esc(it.url) + '">' + (thumb ? '<img src="' + esc(thumb) + '" alt="" width="88" height="88" loading="lazy">' : "") + "</a>" +
          '<div class="npc-itembody">' +
          '<div class="npc-itemtop"><a class="npc-name" href="' + esc(it.url) + '" title="' + esc(it.product_title) + '">' + esc(shortName(it.product_title)) + "</a>" +
          '<button type="button" class="npc-remove" data-act="remove">Remove</button></div>' +
          (sub ? '<div class="npc-variant">' + esc(sub) + "</div>" : "") +
          '<div class="npc-row"><div class="npc-qty">' +
          '<button type="button" data-act="dec" aria-label="Decrease quantity">&minus;</button>' +
          "<span>" + (wanted ? packWant[it.key] : it.quantity) + "</span>" +
          '<button type="button" data-act="inc" aria-label="Increase quantity"' + (atMax ? ' aria-disabled="true" aria-describedby="npc-max-' + n + '"' : "") + ">+</button>" +
          "</div>" +
          '<div class="npc-prices">' + was + '<span class="npc-line">' + money(it.final_line_price, FORMAT) + "</span></div>" +
          "</div>" + (atMax ? '<div class="npc-packmax" id="npc-max-' + n + '">' + PACK_MAX_NOTE + "</div>" : "") +
          "</div>" + (packed ? includedList(kids[it.key], it.quantity) : "") + "</div>";
      });
      html += "</div>";

      var inCart = items.map(function (i) { return i.product_id; });
      var offer = UPSELLS.filter(function (u) { return inCart.indexOf(u.pid) === -1; }).slice(0, 3);
      if (offer.length) {
        html += '<div class="npc-up"><div class="npc-uphead"><b>Complete the stack</b><i></i></div><div class="npc-uplist">';
        offer.forEach(function (u) {
          html +=
            '<div class="npc-uprow">' +
            '<a class="npc-upimg" href="' + esc(u.url) + '">' + (u.img ? '<img src="' + esc(u.img) + '" alt="" width="48" height="48" loading="lazy">' : "") + "</a>" +
            '<div class="npc-upbody"><div class="npc-upname" title="' + esc(u.name) + '">' + esc(shortName(u.name)) + "</div>" +
            (u.sub ? '<div class="npc-upsub">' + esc(u.sub) + "</div>" : "") + "</div>" +
            '<button type="button" class="npc-upadd" data-add="' + esc(u.id) + '">+ ' + money(u.price, FORMAT) + "</button></div>";
        });
        html += "</div></div>";
      }

      html +=
        '<div class="npc-code' + (codeOpen ? " is-open" : "") + '" id="npc-code">' +
        '<button type="button" class="npc-codetoggle" data-act="code-toggle"><span class="npc-plus"></span>' +
        '<span class="npc-codelabel">Have a discount code?</span></button>' +
        '<ul class="npc-codes" aria-label="Discount codes">' + codeChips() + "</ul>" +
        '<div class="npc-codewrap"><div class="npc-codein"><div class="npc-codeform">' +
        '<input type="text" id="npc-code-input" placeholder="Enter code" autocomplete="off" spellcheck="false">' +
        '<button type="button" data-act="code-apply">Apply</button></div>' +
        (codeNote ? '<div class="npc-codenote">' + esc(codeNote) + "</div>" : "") +
        "</div></div></div>";

      scroll.innerHTML = html;
    }

    /* ---- subscribe / one-time: only fetched the first time the cart opens ---- */
    function loadPlans(done) {
      if (plansLoaded) { done && done(); return; }
      plansLoaded = true;
      var items = cart.items || [], todo = [];
      items.forEach(function (i) { if (i.handle && !isComp(i) && !(i.handle in planCache)) todo.push(i.handle); });
      if (!todo.length) { done && done(); return; }
      var left = todo.length;
      todo.forEach(function (h) {
        planCache[h] = null;
        fetch(ROOT + "products/" + h + ".js", { headers: { Accept: "application/json" } })
          .then(function (r) { return r.json(); })
          .then(function (prod) {
            var g = (prod.selling_plan_groups || [])[0];
            var sp = g && g.selling_plans && g.selling_plans[0];
            if (!sp) return;
            var adj = (sp.price_adjustments || [])[0];
            var pct = adj && adj.value_type === "percentage" ? adj.value : null;
            var min = null;
            ((g.options && g.options[0] && g.options[0].values) || []).forEach(function (v) {
              if (String(v).slice(-sp.name.length) === sp.name) {
                var m = String(v).match(/MIN_CYCLES=(\d+)/);
                if (m) min = Number(m[1]);
              }
            });
            planCache[h] = { id: sp.id, name: sp.name, pct: pct, min: min };
          })
          .catch(function () {})
          .then(function () { if (--left === 0) done && done(); });
      });
    }

    function renderPlan() {
      if (!planEl) planEl = $("npc-plan");
      if (!planEl) return;
      var items = cart.items || [];
      /* a pack's components are included in the pack: putting one on a plan would end the pack's code */
      var eligible = items.filter(function (i) { return !isComp(i) && planCache[i.handle]; });
      /* Build a Bundle lines (_bundle_tier) in the cart: no plan switch at all (Adam 19 Oct 2026; as new-cart.liquid) */
      var bundled = items.some(function (i) { return i.properties && i.properties._bundle_tier; });
      if (!items.length || !eligible.length || bundled) { planEl.hidden = true; planEl.innerHTML = ""; return; }
      var info = planCache[eligible[0].handle];
      var subscribed = eligible.every(function (i) { return !!i.selling_plan_allocation; });
      var save = info.pct != null ? "Subscribe · save " + (info.pct % 1 === 0 ? info.pct : info.pct.toFixed(1)) + "%" : "Subscribe";
      var note = info.min ? "Cancel after " + info.min + " orders" : "Cancel anytime";
      planEl.hidden = false;
      planEl.innerHTML =
        '<button type="button" class="npc-planbtn' + (subscribed ? "" : " is-on") + '" data-plan="once" aria-pressed="' + (!subscribed) + '"><b>One-time</b><small>No commitment</small></button>' +
        '<button type="button" class="npc-planbtn' + (subscribed ? " is-on" : "") + '" data-plan="sub" aria-pressed="' + subscribed + '"><b>' + esc(save) + "</b><small>" + esc(info.name ? "Every " + info.name.toLowerCase() + " · " + note.toLowerCase() : note) + "</small></button>";
    }

    function refreshFromServer(open) {
      return fetchJSON(ROOT + "cart.js", { headers: { Accept: "application/json" } }).then(function (c) {
        cart = c;
        applyDesiredOverrides();
        if (rendered) render();
        else renderTotals();
        if (rendered) loadPlans(renderPlan);
        if (open) dialog.open();
        return cart;
      });
    }

    /* ---- quantity taps: a desired-quantity map keyed by the STABLE line
       key (not position — a removal elsewhere never shifts it), one write
       per key in flight at a time, unconfirmed taps re-applied after every
       reply. All of these run through the shared write queue. ---- */
    function changeQty(key, qty) {
      desired[key] = qty;
      applyDesiredOverrides();
      if (rendered) { if (!patchRow(key)) render(); }
      enqueue(function () { return writeQuantity(key); });
    }

    function writeQuantity(key) {
      if (!Object.prototype.hasOwnProperty.call(desired, key)) return Promise.resolve(cart);
      var qty = desired[key];
      return fetchJSON(ROOT + "cart/change.js", {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function (c) {
        cart = c;
        if (desired[key] === qty) delete desired[key];
        applyDesiredOverrides();
        if (rendered) render();
        dispatchCartEvent();
        return cart;
      }).catch(function () {
        return refreshFromServer(false);
      });
    }

    /* a pack's quantity, or its removal: the pack and its components move together in one
       cart/update.js (never one line at a time, which would leave a half pack the code
       cannot price). The card shows the new number and dims until the reply. */
    function changePack(key, qty) {
      packWant[key] = Math.max(0, qty);
      var row = qs(scroll, '[data-key="' + key + '"]');
      if (row) {
        var n = qs(row, ".npc-qty span");
        if (n) n.textContent = packWant[key];
        row.classList.add("is-busy");
      }
      enqueue(function () { return writePack(key); });
    }

    function writePack(key) {
      if (!Object.prototype.hasOwnProperty.call(packWant, key)) return Promise.resolve(cart);
      var qty = packWant[key];
      return fetchJSON(ROOT + "cart.js", { headers: { Accept: "application/json" } }).then(function (fresh) {
        var body = packUpdate(fresh, key, qty);
        return body ? fetchJSON(ROOT + "cart/update.js", { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body) }) : fresh;
      }).then(function (c) {
        cart = c;
        if (packWant[key] === qty) delete packWant[key];
        applyDesiredOverrides();
        if (rendered) render();
        dispatchCartEvent();
        return cart;
      }).catch(function (e) {
        delete packWant[key];
        if (window.npCart && e && e.message) window.npCart.notify(e.message);
        return refreshFromServer(false);
      });
    }

    function addUpsell(variantId) {
      enqueue(function () {
        setBusy(true);
        return fetchJSON(ROOT + "cart/add.js", {
          method: "POST", headers: JSON_HEADERS,
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        }).then(function () { return refreshFromServer(false); })
          .then(function () { dispatchCartEvent(); })
          .catch(function () {})
          .then(function () { setBusy(false); });
      });
    }

    function applyPlan(on) {
      enqueue(function () {
        setBusy(true);
        var guard = 0;
        var step = function () {
          if (++guard > 20) { setBusy(false); return refreshFromServer(false).then(renderPlan); }
          var items = cart.items || [], target = null;
          for (var i = 0; i < items.length; i++) {
            if (isComp(items[i])) continue;
            var pc = planCache[items[i].handle], has = !!items[i].selling_plan_allocation;
            if (on && pc && !has) { target = { key: items[i].key, plan: pc.id, qty: items[i].quantity }; break; }
            if (!on && has) { target = { key: items[i].key, plan: null, qty: items[i].quantity }; break; }
          }
          if (!target) {
            if (rendered) { render(); renderPlan(); }
            dispatchCartEvent();
            setBusy(false);
            return Promise.resolve(cart);
          }
          return fetchJSON(ROOT + "cart/change.js", {
            method: "POST", headers: JSON_HEADERS,
            body: JSON.stringify({ id: target.key, quantity: target.qty, selling_plan: target.plan })
          }).then(function (c) {
            cart = c;
            return step();
          }).catch(function () { setBusy(false); return refreshFromServer(false).then(renderPlan); });
        };
        return step();
      });
    }

    /* the result of a code action: shown under the code form and spoken from the
       live region in the panel (the scroll area is rebuilt, which drops its own) */
    function noteCode(t) {
      codeNote = t;
      var live = $("npc-live");
      if (live) live.textContent = t;
    }

    function codesOnCart() {
      return (cart.discount_codes || []).map(function (d) { return d.code; });
    }

    function applyCode(code) {
      enqueue(function () {
        if (!code) { noteCode("Enter a code to apply."); if (rendered) render(); return Promise.resolve(cart); }
        setBusy(true);
        /* cart/update.js sets the whole list of codes, so the ones already on the cart go with the new one
           (9 Oct pre-launch check, Adam: fix them all). Before, any code typed here, even a mistyped one,
           dropped the pack's own code and put its components back to full price. */
        var codes = codesOnCart();
        if (!codes.some(function (c) { return c.toLowerCase() === code.toLowerCase(); })) codes.push(code);
        return fetchJSON(ROOT + "cart/update.js", {
          method: "POST", headers: JSON_HEADERS,
          body: JSON.stringify({ discount: codes.join(",") })
        }).then(function (c) {
          cart = c;
          var apps = c.discount_codes || [];
          var mine = apps.filter(function (d) { return String(d.code).toLowerCase() === code.toLowerCase(); })[0];
          if (mine) noteCode(mine.applicable ? code.toUpperCase() + " applied." : code.toUpperCase() + " is not valid for this cart.");
          /* the code is judged only on its own entry: an automatic discount
             already on the cart says nothing about whether this code worked */
          else noteCode(code.toUpperCase() + " is not valid for this cart.");
          codeOpen = true;
          if (rendered) render();
          dispatchCartEvent();
        }).catch(function () {
          noteCode("That code could not be applied.");
          if (rendered) render();
        }).then(function () { setBusy(false); });
      });
    }

    /* the chip's remove button: the same update with that one code left out */
    function removeCode(code, at) {
      enqueue(function () {
        setBusy(true);
        var rest = codesOnCart().filter(function (c) { return c.toLowerCase() !== code.toLowerCase(); });
        return fetchJSON(ROOT + "cart/update.js", {
          method: "POST", headers: JSON_HEADERS,
          body: JSON.stringify({ discount: rest.join(",") })
        }).then(function (c) {
          cart = c;
          noteCode(code.toUpperCase() + " removed.");
          if (rendered) render();
          dispatchCartEvent();
        }).catch(function () {
          noteCode("That code could not be removed.");
          if (rendered) render();
        }).then(function () {
          setBusy(false);
          /* the button that was pressed is gone: put focus on the next chip, or the code control */
          var next = qsa(scroll, ".npc-chipx")[at] || qsa(scroll, ".npc-chipx").pop() || qs(scroll, ".npc-codetoggle");
          if (next) next.focus();
        });
      });
    }

    /* --- events --- */
    if (scroll) {
      scroll.addEventListener("click", function (e) {
        var addBtn = e.target.closest("[data-add]");
        if (addBtn) { addUpsell(addBtn.getAttribute("data-add")); return; }
        var act = e.target.closest("[data-act]");
        if (!act) return;
        var kind = act.getAttribute("data-act");
        if (kind === "code-toggle") { codeOpen = !codeOpen; var codeBox = $("npc-code"); if (codeBox) codeBox.classList.toggle("is-open", codeOpen); return; }
        if (kind === "code-apply") { var inputEl = $("npc-code-input"); applyCode(((inputEl && inputEl.value) || "").trim()); return; }
        if (kind === "code-remove") {
          var chip = act.closest(".npc-chip");
          removeCode(act.getAttribute("data-code") || "", qsa(scroll, ".npc-chip").indexOf(chip));
          return;
        }
        var row = act.closest("[data-key]");
        if (!row) return;
        var key = row.getAttribute("data-key");
        var item = (cart.items || []).filter(function (i) { return i.key === key; })[0];
        /* a pack (with its components in the cart) goes through changePack, everything else through changeQty */
        var asPack = item && isPack(item) && ownedComps(cart.items || [], item).length > 0;
        var currentQty = Object.prototype.hasOwnProperty.call(packWant, key) ? packWant[key]
          : Object.prototype.hasOwnProperty.call(desired, key) ? desired[key] : (item ? item.quantity : 0);
        var change = asPack ? changePack : changeQty;
        /* a pack goes no higher than the order limit (the + is also marked aria-disabled once it is reached) */
        if (kind === "inc" && item && isPack(item) && currentQty + 1 - item.quantity > packRoom(cart.items || [], item)) return;
        if (kind === "inc") change(key, currentQty + 1);
        else if (kind === "dec") change(key, currentQty - 1);
        else if (kind === "remove") change(key, 0);
      });
      scroll.addEventListener("keydown", function (e) {
        if (e.key === "Enter" && e.target.id === "npc-code-input") { e.preventDefault(); applyCode((e.target.value || "").trim()); }
      });
    }

    var planContainer = $("npc-plan");
    if (planContainer) planContainer.addEventListener("click", function (e) {
      var b = e.target.closest("[data-plan]");
      if (b) applyPlan(b.getAttribute("data-plan") === "sub");
    });

    var closeBtn = $("npc-close");
    var dialog = new window.npChromeUtil.Dialog(panel, scrim, {
      onFirstOpen: function () { render(); loadPlans(renderPlan); },
      onOpen: wireConsent
    });
    if (closeBtn) closeBtn.addEventListener("click", function () { dialog.close(); });

    /* Shopify injects <small id="shopify-buyer-consent"> next to the wallet
       buttons when the cart holds a subscription line. It is a consent
       notice, so it stays reachable; folded behind a labelled toggle rather
       than printed in full. Narrowed to .npc-wallets (not the whole footer). */
    function wireConsent() {
      var c = qs(panel, "#npc .npc-wallets #shopify-buyer-consent, .npc-wallets #shopify-buyer-consent");
      if (!c) return;
      var host = c.parentElement;
      if (!host || host.querySelector(".npc-consent-toggle")) return;
      var b = document.createElement("button");
      b.type = "button";
      b.className = "npc-consent-toggle";
      b.setAttribute("aria-expanded", "false");
      b.setAttribute("aria-controls", "shopify-buyer-consent");
      b.innerHTML = '<span class="npc-plus"></span><span>Subscription terms</span>';
      host.insertBefore(b, c);
      b.addEventListener("click", function () {
        var on = !host.classList.contains("is-open");
        host.classList.toggle("is-open", on);
        b.setAttribute("aria-expanded", on ? "true" : "false");
      });
    }
    var wallets = qs(panel, ".npc-wallets");
    if (wallets && window.MutationObserver) {
      new MutationObserver(wireConsent).observe(wallets, { childList: true, subtree: true });
    }

    /* ---- cart hub: window.npSlideCart ---- */
    window.npSlideCart = {
      open: function () { refreshFromServer(false).then(function () { dialog.open(); }); },
      close: function () { dialog.close(); },
      refresh: function (open) { return refreshFromServer(!!open); }
    };

    /* np:cart (from npCart.add, or any other section's own writes): sync
       state and counts without opening */
    document.documentElement.addEventListener("np:cart", function (e) {
      var c = e.detail && e.detail.cart;
      if (!c || !c.items) return;
      cart = c;
      applyDesiredOverrides();
      renderTotals();
      if (rendered) render();
    });
    /* legacy templates (e.g. crossfit-games) still dispatch this: refresh
       then open */
    document.documentElement.addEventListener("cart:refresh", function () {
      refreshFromServer(true);
    });

    renderTotals();
  })();
})();

/* ---- swipe hints ------------------------------------------------------------
   Any horizontal scroller marked [data-np-swipe] shows that it scrolls while its
   content overflows: a short position bar under it (default), or with
   data-np-swipe="fade" a fade on the edge that still has content (chip rows).
   Article tables are tagged here because their markup comes from the editor.
   Rows built later by other scripts: call window.npSwipe() after rendering. */
(function () {
  var rows = [];

  function lightText(el) {
    var m = getComputedStyle(el).color.match(/\d+(\.\d+)?/g);
    return !!m && 0.299 * m[0] + 0.587 * m[1] + 0.114 * m[2] > 160;
  }

  function update(s) {
    var el = s.el, max = el.scrollWidth - el.clientWidth, over = max > 4;
    if (s.bar) {
      s.bar.hidden = !over;
      if (!over) return;
      var w = Math.max(el.clientWidth / el.scrollWidth, 0.18);
      s.thumb.style.width = w * 100 + "%";
      s.thumb.style.transform = "translateX(" + (el.scrollLeft / max) * (1 - w) / w * 100 + "%)";
    } else {
      el.classList.toggle("np-swipe-start", over && el.scrollLeft > 4);
      el.classList.toggle("np-swipe-end", over && el.scrollLeft < max - 4);
    }
  }

  function setup(el) {
    if (el.npSwipe) return;
    var s = { el: el, raf: 0 };
    if (el.getAttribute("data-np-swipe") !== "fade") {
      s.bar = document.createElement("div");
      s.bar.className = "np-swipe" + (lightText(el) ? " np-swipe--dark" : "");
      s.bar.setAttribute("aria-hidden", "true");
      s.thumb = s.bar.appendChild(document.createElement("span"));
      el.insertAdjacentElement("afterend", s.bar);
    }
    el.npSwipe = s;
    el.addEventListener("scroll", function () {
      if (!s.raf) s.raf = requestAnimationFrame(function () { s.raf = 0; update(s); });
    }, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(function () { update(s); }).observe(el);
    rows.push(s);
    update(s);
  }

  function scan() {
    document.querySelectorAll(".na-body table").forEach(function (t) {
      if (!t.hasAttribute("data-np-swipe")) t.setAttribute("data-np-swipe", "");
    });
    document.querySelectorAll("[data-np-swipe]").forEach(setup);
    rows.forEach(update);
  }

  window.npSwipe = scan;
  scan();
  window.addEventListener("load", scan);
})();
